import { CreatePersonalReminder } from "../reminders/CreatePersonalReminder";
import { useState, useEffect, useRef, useCallback } from "react";
import { ScrollText, X } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { usePatchQueue } from "@/lib/usePatchQueue";
import { useCallGuide } from "@/lib/useCallGuide";
import { buildCallRecap, latestActiveQuoteAmount } from "@/lib/callScript";
import { caseDrawerTitle, requestDetailsFromMessages } from "@/lib/casePresentation";
import { CallScriptDrawer } from "./CallScriptDrawer";
import { estimatedTotal, valueTier } from "@/lib/caseValue";
import { Case } from "@/lib/useRealtimeCases";
import { supabase } from "@/lib/supabaseClient";
import { statusColors, orderedStatuses, CaseStatus } from "@/lib/statusColors";
import { ConversationFeed } from "./ConversationFeed";
import { QuoteUpload } from "./QuoteUpload";
import { InvoiceUpload } from "./InvoiceUpload";
import { ActivityLog } from "./ActivityLog";
import { MultiSelectDropdown } from "../ui/MultiSelectDropdown";
import { PdfPreviewModal } from "./PdfPreviewModal";
import { CLIENT_TYPE_OPTIONS, normalizeClientType } from "@/lib/clientTypes";
import { detectBrandFromCenter, getCentersForBrand, mergeCenterOptions } from "@/lib/brandConfig";
import {
  parisDateTimeToUtcIso,
  formatParisDate,
  formatParisTime,
  toParisDateInputValue,
  toParisTimeInputValue,
} from "@/lib/parisDateTime";

import { ACTIVITIES_OPTIONS, EXTRAS_OPTIONS } from "@/lib/caseOptions";

type Tab = "summary" | "messages" | "quotes" | "invoices" | "logs";

interface CaseDrawerProps {
  case: Case;
  onClose: () => void;
  initialTab?: Tab;
}

export function CaseDrawer(props: CaseDrawerProps) {
  return <CaseDrawerContent key={props.case.id} {...props} />;
}

function CaseDrawerContent({ case: initialCase, onClose, initialTab = "summary" }: CaseDrawerProps) {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [caseData, setCaseData] = useState<Case>(initialCase);
  const [caseLoaded, setCaseLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadRevision, setLoadRevision] = useState(0);
  const [scriptOpen, setScriptOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);
  const [recapMessage, setRecapMessage] = useState("");
  const recapLock = useRef(false);
  const lastRecap = useRef<{ body: string; block: string } | null>(null);
  const [requestDetails, setRequestDetails] = useState({ payment: null as string | null, discovery: null as string | null });
  const [salesperson, setSalesperson] = useState("");
  const [quoteAmount, setQuoteAmount] = useState<number | null>(null);
  const scriptButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const casePanel = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const call = useCallGuide(initialCase.id);
  const save = usePatchQueue<Case>(async updates => {
    const { error } = await supabase.from("cases").update(updates).eq("id", initialCase.id).select("id").single();
    if (error) throw error;
    if (updates.status === "Signé") {
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session?.access_token) void fetch("/api/quotes/extract-amount", {
          method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${data.session.access_token}` },
          body: JSON.stringify({ caseId: initialCase.id }),
        }).catch(() => {});
      });
    }
  });
  const saveStatus = save.status === "error" || call.save.status === "error" ? "error"
    : save.status === "saving" || call.save.status === "saving" ? "saving"
    : save.status === "saved" || call.save.status === "saved" ? "saved" : "idle";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data, error } = await supabase.from("cases").select("*").eq("id", initialCase.id).single();
        if (cancelled) return;
        if (error || !data) throw error;
        setCaseData({ ...data, client_type: normalizeClientType(data.client_type), ...save.unsaved() });
        setCaseLoaded(true); setLoadError(false);
      } catch { if (!cancelled) setLoadError(true); }
    };
    void load();
    return () => { cancelled = true; };
  }, [initialCase.id, loadRevision]);
  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);
  useEffect(() => {
    let cancelled = false;
    void supabase.from("messages").select("content,threads!inner(case_id)")
      .eq("threads.case_id", initialCase.id).eq("sender_type", "sales")
      .in("sender_name", ["Système Fillout", "Demande du site"])
      .order("created_at", { ascending: true }).limit(5).then(({ data }) => {
        if (!cancelled) setRequestDetails(requestDetailsFromMessages(data || []));
      });
    return () => { cancelled = true; };
  }, [initialCase.id]);

  useEffect(() => {
    let cancelled = false;
    const fallback = (user?.user_metadata?.full_name || user?.user_metadata?.first_name || "") as string;
    setSalesperson(typeof fallback === "string" ? fallback.trim().split(/\s+/)[0] : "");
    if (user?.id) void supabase.from("profiles").select("full_name").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (!cancelled && data?.full_name) setSalesperson(data.full_name.trim().split(/\s+/)[0]);
    });
    return () => { cancelled = true; };
  }, [user?.id]);
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      const { data, error } = await supabase.from("quotes").select("id,version,created_at,amount_ht,archived_at").eq("case_id", initialCase.id).is("archived_at", null).order("version", { ascending: false }).order("created_at", { ascending: false }).order("id", { ascending: false }).limit(1);
      if (!cancelled) setQuoteAmount(error ? null : latestActiveQuoteAmount(data || []));
    };
    void refresh();
    const channel = supabase.channel(`call-quote:${initialCase.id}`).on("postgres_changes", { event: "*", schema: "public", table: "quotes", filter: `case_id=eq.${initialCase.id}` }, () => { void refresh(); }).subscribe();
    window.addEventListener("documents-changed", refresh);
    return () => { cancelled = true; window.removeEventListener("documents-changed", refresh); void supabase.removeChannel(channel); };
  }, [initialCase.id]);
  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden"; closeButton.current?.focus();
    return () => { document.body.style.overflow = overflow; previousFocus?.focus(); };
  }, []);

  const handleFieldsChange = useCallback((updates: Partial<Case>, _key: string) => {
    setCaseData(current => ({ ...current, ...updates })); save.enqueue(updates);
  }, [save.enqueue]);
  const handleFieldChange = useCallback((field: keyof Case, value: unknown) => {
    const patch = field === "participants" ? { participants: value, pax: value }
      : field === "phone_reminder_date" && !value ? { phone_reminder_date: null, phone_reminder_time: null } : { [field]: value };
    handleFieldsChange(patch as Partial<Case>, field);
  }, [handleFieldsChange]);
  const flushAll = async () => {
    const results = await Promise.all([save.flush(), call.save.flush()]);
    return results.every(Boolean);
  };
  const close = async () => {
    if (closing || recapLock.current) return;
    setClosing(true);
    if (await flushAll()) onClose(); else setClosing(false);
  };
  const closeScript = () => { setScriptOpen(false); scriptButton.current?.focus(); };
  const openNotes = () => {
    setActiveTab("summary");
    requestAnimationFrame(() => { casePanel.current?.querySelector<HTMLTextAreaElement>("#case-internal-notes")?.focus(); });
  };
  const saveRecap = async () => {
    if (recapLock.current || !caseLoaded || call.loading || call.loadError || loadError) return;
    recapLock.current = true;
    setRecapBusy(true); setRecapMessage("");
    const body = buildCallRecap(caseData, call.guide);
    try {
      if (!(await flushAll())) throw new Error("Les saisies n’ont pas pu être enregistrées. Réessayez.");
      if (!body) throw new Error("Renseignez le script avant de créer un récapitulatif.");
      if (lastRecap.current?.body !== body) {
        const now = new Date();
        lastRecap.current = { body, block: `Récapitulatif d’appel — ${formatParisDate(now)} à ${formatParisTime(now)}${salesperson ? ` — ${salesperson}` : ""}\n${body}` };
      }
      const block = lastRecap.current!.block;
      const { data: notes, error } = await supabase.rpc("append_case_call_recap", { p_case_id: initialCase.id, p_recap: block });
      if (error || typeof notes !== "string") throw new Error("Le récapitulatif n’a pas pu être enregistré. Réessayez.");
      setCaseData(current => ({ ...current, internal_notes: notes }));
      setRecapMessage("Récapitulatif enregistré dans les notes internes."); openNotes();
    } catch (error) {
      setRecapMessage(error instanceof Error ? error.message : "Enregistrement impossible.");
    } finally {
      recapLock.current = false; setRecapBusy(false);
    }
  };

  return <>
    <div className="fixed inset-0 bg-black/50 z-40" onClick={() => { void close(); }} aria-hidden="true" />
    <div role="dialog" aria-modal="true" aria-labelledby="case-drawer-title" className={`case-workspace fixed inset-0 z-50 flex justify-end pointer-events-none ${scriptOpen ? "with-call-script" : ""}`}
      onKeyDown={e => {
        if (e.defaultPrevented || (e.target as HTMLElement).closest('[role="dialog"]') !== e.currentTarget) return;
        if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); if (scriptOpen) closeScript(); else void close(); }
        if (e.key === "Tab") {
          const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]')).filter(el => el.getClientRects().length && !el.closest('fieldset:disabled'));
          const first = items[0], last = items[items.length-1];
          if (e.shiftKey && (document.activeElement === first || !items.includes(document.activeElement as HTMLElement))) { e.preventDefault(); last?.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
      }}>
      {scriptOpen && <CallScriptDrawer caseData={caseData} salesperson={salesperson} quoteAmount={quoteAmount} guide={call.guide} onGuideChange={call.change} onFieldsChange={handleFieldsChange} onClose={closeScript} onNotes={openNotes} onRecap={() => void saveRecap()} recapBusy={recapBusy} recapMessage={recapMessage} loading={!caseLoaded || call.loading} error={loadError || call.loadError} disabled={closing || recapBusy} onRetry={() => { setLoadRevision(n=>n+1); call.reload(); }} />}
      <div ref={casePanel} className="case-detail-panel pointer-events-auto min-h-0 bg-white shadow-2xl flex flex-col">
        <div className="shrink-0 border-b border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h2 id="case-drawer-title" className="text-lg font-semibold leading-snug text-gray-900 break-words">{caseDrawerTitle(caseData)}</h2>
            <div role="status" aria-live="polite" className="min-h-4 text-xs">
              {saveStatus === "saving" && <span className="text-gray-500">Enregistrement…</span>}
              {saveStatus === "saved" && <span className="text-green-700">✓ Sauvegardé</span>}
              {saveStatus === "error" && <span className="text-red-700">Modifications non enregistrées. <button type="button" onClick={() => { void flushAll(); }} className="font-medium underline">Réessayer</button></span>}
            </div>
          </div>
          <CreatePersonalReminder caseId={caseData.id} title={caseData.title || "Dossier sans titre"} />
          <button ref={scriptButton} type="button" aria-expanded={scriptOpen} aria-controls="case-call-script" onClick={() => scriptOpen ? closeScript() : setScriptOpen(true)} className={`inline-flex shrink-0 items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium ${scriptOpen ? "border-indigo-200 bg-indigo-50 text-indigo-800" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}><ScrollText size={16} />Script{((quoteAmount != null && quoteAmount>1500) || valueTier(caseData) !== null) && <span className="h-2 w-2 rounded-full bg-amber-500" aria-label="Appel à prioriser : devis supérieur à 1 500 euros HT ou demande de 2 500 euros HT et plus" />}</button>
          <button ref={closeButton} type="button" onClick={() => { void close(); }} disabled={closing || recapBusy} aria-label="Fermer le dossier" className="shrink-0 rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"><X size={20} /></button>
        </div>
        <ValueAlert caseData={caseData} />
        <div className="shrink-0 border-b border-gray-200 overflow-x-auto">
          <div className="flex min-w-max">
            {[{id:"summary",label:"Résumé"},{id:"messages",label:"Messages"},{id:"quotes",label:"Devis"},{id:"invoices",label:"Factures"},{id:"logs",label:"Logs"}].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as Tab)} className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab===tab.id ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500 hover:text-gray-700"}`}>{tab.label}</button>)}
          </div>
        </div>
        {loadError && <div role="alert" className="bg-red-50 p-3 text-sm text-red-700">Impossible de charger la fiche complète. <button type="button" onClick={() => setLoadRevision(n=>n+1)} className="underline">Réessayer</button></div>}
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {activeTab === "summary" && <fieldset disabled={!caseLoaded || closing || recapBusy} className="min-w-0 disabled:opacity-60"><SummaryTab caseData={caseData} requestDetails={requestDetails} onChange={handleFieldChange} onFieldsChange={handleFieldsChange} /></fieldset>}
          {activeTab === "messages" && <ConversationFeed caseId={caseData.id} />}
          {activeTab === "quotes" && <QuoteUpload caseId={caseData.id} />}
          {activeTab === "invoices" && <InvoiceUpload caseId={caseData.id} />}
          {activeTab === "logs" && <ActivityLog caseId={caseData.id} />}
        </div>
      </div>
    </div>
  </>;
}

interface SummaryTabProps {
  caseData: Case;
  requestDetails: { payment: string | null; discovery: string | null };
  onChange: (field: keyof Case, value: any) => void;
  onFieldsChange: (updates: Partial<Case>, debounceKey: string) => void;
}

function SummaryTab({ caseData, requestDetails, onChange, onFieldsChange }: SummaryTabProps) {
  const [airtableQuotes, setAirtableQuotes] = useState<string | null>(null);
  const [airtablePdfUrls, setAirtablePdfUrls] = useState<string[]>([]);
  const [showAirtableModal, setShowAirtableModal] = useState(false);
  const [availableCenters, setAvailableCenters] = useState<string[]>([]);
  const eventTimeInputValue =
    caseData.event_time_provided === false
      ? ""
      : toParisTimeInputValue(caseData.event_date);

  // Charger les centres distincts existants
  useEffect(() => {
    const fetchCenters = async () => {
      const caseBrand = caseData.brand || detectBrandFromCenter(caseData.center);
      const { data } = await supabase
        .from("cases")
        .select("center")
        .not("center", "is", null);
      if (data) {
        setAvailableCenters(
          mergeCenterOptions(getCentersForBrand(caseBrand), data.map((r) => r.center), [caseData.secondary_center]),
        );
      }
    };
    fetchCenters();
  }, [caseData.brand, caseData.center, caseData.secondary_center]);

  // Charger old_airtable_quotes + récupérer les URLs migrées
  useEffect(() => {
    const fetchAirtableData = async () => {
      setAirtableQuotes(caseData.old_airtable_quotes || null);
      
      if (caseData.old_airtable_quotes) {
        // Récupérer les devis migrés (version 1) depuis la table quotes
        const { data: migratedQuotes } = await supabase
          .from("quotes")
          .select("file_url")
          .eq("case_id", caseData.id)
          .eq("version", 1)
          .is("archived_at", null)
          .order("group_position", { ascending: true });
        
        if (migratedQuotes && migratedQuotes.length > 0) {
          setAirtablePdfUrls(migratedQuotes.map((q) => q.file_url));
        }
      }
    };
    fetchAirtableData();
  }, [caseData.old_airtable_quotes, caseData.id]);

  return (
    <div className="p-6 space-y-6">

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="min-w-0 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <label htmlFor="case-internal-notes" className="mb-2 block text-sm font-semibold text-gray-900">Notes internes</label>
          <textarea id="case-internal-notes" value={caseData.internal_notes || ""}
            onChange={e => onChange("internal_notes", e.target.value || null)}
            placeholder="Notes et récapitulatifs d’appel pour l’équipe…" rows={7}
            className="min-h-44 w-full resize-y rounded-md border border-amber-200 bg-white p-3 text-sm leading-relaxed focus:ring-2 focus:ring-amber-500" />
          <p className="mt-1 text-xs text-gray-600">Privées, réservées à l’équipe commerciale.</p>
        </section>
        <section className="min-w-0 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <label htmlFor="case-client-notes" className="mb-2 block text-sm font-semibold text-gray-900">Commentaires du client</label>
          <textarea id="case-client-notes" value={caseData.client_notes || ""}
            onChange={e => onChange("client_notes", e.target.value || null)}
            placeholder="Précisions fournies par le client…" rows={7}
            className="min-h-44 w-full resize-y rounded-md border border-gray-300 bg-white p-3 text-sm leading-relaxed focus:ring-2 focus:ring-indigo-500" />
          <p className="mt-1 text-xs text-gray-600">Informations complémentaires de sa demande.</p>
        </section>
      </div>
      <dl className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-2">
        <div><dt className="text-xs font-medium text-gray-500">Mode de paiement choisi</dt>
          <dd className="mt-1 text-sm font-semibold text-gray-900">{caseData.payment_methods?.join(", ") || requestDetails.payment || "Non renseigné"}</dd></div>
        {(caseData.discovery_source || requestDetails.discovery) && <div>
          <dt className="text-xs font-medium text-gray-500">Comment nous avez-vous connus ?</dt>
          <dd className="mt-1 text-sm font-semibold text-gray-900">{caseData.discovery_source || requestDetails.discovery}</dd>
        </div>}
      </dl>

      {/* Titre du dossier */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Titre du dossier
        </label>
        <input
          type="text"
          value={caseData.title}
          onChange={(e) => onChange("title", e.target.value)}
          className="w-full border border-gray-300 rounded-md p-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Statut */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Statut du dossier
        </label>
        <select
          value={caseData.status}
          onChange={(e) => onChange("status", e.target.value as CaseStatus)}
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
          style={{
            backgroundColor: statusColors[caseData.status] || "#F9FAFB",
          }}
        >
          {orderedStatuses.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
      </div>

      {/* Marque */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Marque
        </label>
        <select
          value={caseData.brand || "smile"}
          onChange={(e) => onChange("brand", e.target.value as "smile" | "laser" | "vertical")}
          className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
        >
          <option value="smile">🎳 Smile World</option>
          <option value="laser">🔫 Laser World</option>
          <option value="vertical">🧗 Vertical World</option>
        </select>
      </div>

      {/* Bouton Voir devis Airtable */}
      {airtableQuotes && airtablePdfUrls.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-amber-800">
              <span className="text-lg">📋</span>
              <span className="font-medium">
                {airtablePdfUrls.length} devis importé{airtablePdfUrls.length > 1 ? "s" : ""} depuis Airtable
              </span>
            </div>
            <button
              onClick={() => setShowAirtableModal(true)}
              className="px-3 py-1 text-sm font-medium text-amber-700 hover:text-amber-900 hover:bg-amber-100 rounded-md transition-colors"
            >
              📄 Voir
            </button>
          </div>
        </div>
      )}

      {/* Informations client */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Client</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Prénom
            </label>
            <input
              type="text"
              value={caseData.client_first_name || ""}
              onChange={(e) => onChange("client_first_name", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom
            </label>
            <input
              type="text"
              value={caseData.client_last_name || ""}
              onChange={(e) => onChange("client_last_name", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              value={caseData.client_email || ""}
              onChange={(e) => onChange("client_email", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              value={caseData.client_phone || ""}
              onChange={(e) => onChange("client_phone", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Entreprise */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Entreprise</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'entreprise
            </label>
            <input
              type="text"
              value={caseData.company_name || ""}
              onChange={(e) => onChange("company_name", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse
            </label>
            <input
              type="text"
              value={caseData.address || ""}
              onChange={(e) => onChange("address", e.target.value)}
              placeholder="Rue, numéro..."
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ville
            </label>
            <input
              type="text"
              value={caseData.city || ""}
              onChange={(e) => onChange("city", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Code postal
            </label>
            <input
              type="text"
              value={caseData.postal_code || ""}
              onChange={(e) => onChange("postal_code", e.target.value)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Événement */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Événement</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Centre
            </label>
            <select
              value={caseData.center || ""}
              onChange={(e) => onChange("center", e.target.value || null)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Sélectionner un centre —</option>
              {availableCenters.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              2e choix de centre
            </label>
            <select
              value={caseData.secondary_center || ""}
              onChange={(e) => onChange("secondary_center", e.target.value || null)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Aucun 2e choix —</option>
              {availableCenters.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type de client
            </label>
            <select
              value={caseData.client_type || ""}
              onChange={(e) => onChange("client_type", e.target.value || null)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Non renseigné —</option>
              {CLIENT_TYPE_OPTIONS.map((clientType) => (
                <option key={clientType} value={clientType}>
                  {clientType}
                </option>
              ))}
            </select>
          </div>
          
          {/* Activités souhaitées */}
          <div className="col-span-2">
            <MultiSelectDropdown
              label="Activités souhaitées"
              value={caseData.activities || []}
              onChange={(value) => onChange("activities", value)}
              options={ACTIVITIES_OPTIONS}
              placeholder="Sélectionner des activités..."
              allowCustom={true}
            />
          </div>

          {/* Extras */}
          <div className="col-span-2">
            <MultiSelectDropdown
              label="Extras demandés"
              value={caseData.extras || []}
              onChange={(value) => onChange("extras", value)}
              options={EXTRAS_OPTIONS}
              placeholder="Sélectionner des extras..."
              allowCustom={true}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de l'événement (heure de Paris)
            </label>
            <input
              type="date"
              value={toParisDateInputValue(caseData.event_date)}
              onChange={(e) => {
                if (e.target.value) {
                  onFieldsChange(
                    {
                      event_date: parisDateTimeToUtcIso(
                        e.target.value,
                        eventTimeInputValue || "00:00",
                      ),
                    },
                    "event_date",
                  );
                } else {
                  onFieldsChange(
                    {
                      event_date: null,
                      event_time_provided: false,
                    },
                    "event_date",
                  );
                }
              }}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heure de l'événement (format 24h, Paris)
            </label>
            <input
              type="time"
              value={eventTimeInputValue}
              onChange={(e) => {
                const dateStr = toParisDateInputValue(caseData.event_date);
                if (dateStr) {
                  onFieldsChange(
                    {
                      event_date: parisDateTimeToUtcIso(dateStr, e.target.value || "00:00"),
                      event_time_provided: Boolean(e.target.value),
                    },
                    "event_time",
                  );
                }
              }}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date alternative (facultatif, heure de Paris)
            </label>
            <input
              type="date"
              value={toParisDateInputValue(caseData.alt_date)}
              onChange={(e) => {
                const timeStr = toParisTimeInputValue(caseData.alt_date) || "00:00";
                onChange("alt_date", e.target.value ? parisDateTimeToUtcIso(e.target.value, timeStr) : null);
              }}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Heure alternative (format 24h, Paris)
            </label>
            <input
              type="time"
              value={toParisTimeInputValue(caseData.alt_date)}
              onChange={(e) => {
                const dateStr = toParisDateInputValue(caseData.alt_date);
                if (dateStr && e.target.value) {
                  onChange("alt_date", parisDateTimeToUtcIso(dateStr, e.target.value));
                }
              }}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de participants
            </label>
            <input
              type="number"
              value={caseData.participants ?? caseData.pax ?? ""}
              onChange={(e) => onChange("participants", parseInt(e.target.value) || null)}
              placeholder="0"
              min="0"
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget par participant (€)
            </label>
            <input
              type="number"
              value={caseData.budget_per_person || ""}
              onChange={(e) => onChange("budget_per_person", parseInt(e.target.value) || null)}
              placeholder="0"
              min="0"
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* Suivi commercial */}
      <div className="border-t border-gray-200 pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Suivi commercial
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget (€)
            </label>
            <input
              type="number"
              value={caseData.budget || ""}
              onChange={(e) => onChange("budget", parseFloat(e.target.value) || null)}
              placeholder="0"
              min="0"
              step="100"
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source
            </label>
            <input
              type="text"
              value={caseData.source || ""}
              onChange={(e) => onChange("source", e.target.value)}
              placeholder="Ex: Site web, recommandation..."
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de relance téléphonique
            </label>
            <input
              type="date"
              value={caseData.phone_reminder_date || ""}
              onChange={(e) => onChange("phone_reminder_date", e.target.value || null)}
              className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Le dossier apparaîtra dans les actions « À faire » à cette date.
            </p>
          </div>
          <label className="block text-sm font-medium text-gray-700 col-span-2">
            Heure de relance (Paris)
            <input type="time" value={caseData.phone_reminder_time?.slice(0, 5) || ""} disabled={!caseData.phone_reminder_date}
              onChange={e => onChange("phone_reminder_time", e.target.value || null)} className="mt-1 w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100" />
          </label>
        </div>
      </div>

      {/* Modal preview des devis Airtable */}
      <PdfPreviewModal
        isOpen={showAirtableModal}
        onClose={() => setShowAirtableModal(false)}
        pdfUrls={airtablePdfUrls}
        title="Devis Airtable"
      />
    </div>
  );
}

const eurosRounded = (amount: number) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(amount);

/** Sales rule (Oct 2026): call every client from 2 500 € HT, priority handling from 10 000 € HT. */
function ValueAlert({ caseData }: { caseData: Case }) {
  const tier = valueTier(caseData);
  const total = estimatedTotal(caseData);
  if (!tier || total === null) return null;
  return tier === "priority"
    ? <div role="note" className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"><strong>Demande prioritaire · {eurosRounded(total)} HT</strong> — traitement en priorité et appel du client avant tout devis.</div>
    : <div role="note" className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900"><strong>Appel obligatoire · {eurosRounded(total)} HT</strong> — appeler le client avant d’envoyer le devis.</div>;
}
